import {
  Component,
  OnInit,
  Renderer2,
  ElementRef,
  DOCUMENT,
  inject,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title, Meta } from "@angular/platform-browser";
import { CmpAuthService, EnvSetupObject } from "icamppro-shared-components";
import {
  CmpFamilyMemberObject,
  CmpRememberUserCamper,
} from "../../objects/cmp-family-member.object";
import { LocalStorageService } from "../../services/local-storage.service";
import { environment } from "../../environments/environment";
import { LayoutService } from "../../services/layout.service";
import { CmpAlertModalObject } from "../../objects/cmp-alert.object";
import { CmpAppSettings } from "../../services/cmp-app-settings.service";
import { AnimationOptions } from "ngx-lottie";
import { Observable, interval } from "rxjs";
import { map, startWith, take } from "rxjs/operators";
import { MaintenanceModeService } from "src/services/maintenance-mode.service";
import { LoaderDotsObject } from "@icamppro-customer-portal/shared/cmp-base";
import { CMP_ENV_SETUP } from "../cmp-env-setup.injection-token";

@Component({
  standalone: false,
  selector: "cmp-layout",
  templateUrl: "./layout.component.html",
})
export class LayoutComponent implements OnInit {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private authService = inject(CmpAuthService);
  private localStorageService = inject(LocalStorageService);
  layoutService = inject(LayoutService);
  elRef = inject(ElementRef);
  renderer = inject(Renderer2);
  private cmpAppSettings = inject(CmpAppSettings);
  private route = inject(ActivatedRoute);
  maintenanceModeService = inject(MaintenanceModeService);
  cmpEnv = inject<EnvSetupObject>(CMP_ENV_SETUP);
  private document = inject<Document>(DOCUMENT);

  // PUBLIC
  public assetPrefix = environment.assetPrefix;
  public showTermsModal = false;
  public alertModalObject = new CmpAlertModalObject();
  public rememberCamper: CmpRememberUserCamper = new CmpRememberUserCamper();
  public companyName = "";
  public currentLoadingText$: Observable<string>;
  public options: AnimationOptions = {
    renderer: "svg",
    loop: true,
    autoplay: true,
    // path: environment.production ? '/gateway/loader-dots.json' : '/loader-dots.json',
    animationData: LoaderDotsObject,
  };
  // For now we using for express registration
  public logo = "";

  // PRIVATE
  public userDetail: CmpFamilyMemberObject | null;
  public currentYear: string = new Date().getFullYear().toString();
  public expandHelpText = false;

  constructor() {
    this.layoutService.isAccountCreationLoader = false;
    this.setLoaderText();
    const userLocalStorage = this.localStorageService.getItem("consumer");
    this.userDetail = userLocalStorage ? JSON.parse(userLocalStorage) : null;
    this.layoutService.checkWhetherIsLogin();
    this.loadPortalSettings();
    this.configureSearchEngineIndexing();
    this.setCanonicalURL();
  }

  /**
   * Allow the user to access the page only if the token is added in localStorage
   * Return back to home page if the token is unavailable
   * Add menu items to show in sidebar
   * @return void
   */
  ngOnInit() {
    const changePasswordToken = this.route.snapshot.queryParamMap.get("token");
    if (changePasswordToken) {
      this.layoutService.accessUrl = "/";
    }
    const localStorageStoredValues = [
      "remember_camper",
      "camper",
      "CMP_CAMPER",
      "CMP_CAMPER_PORTAL",
      "CMP_CAMPER_SHADOW_ID",
      "CMP_CAMPER_SHADOW_USER",
    ];
    const containsExistingLocalStorage = localStorageStoredValues.some((s) => {
      return this.localStorageService.getItem(s) ? true : false;
    });
    if (containsExistingLocalStorage) {
      this.authService.logOut();
    }
    this.checkToken();

    this.localStorageService.removeItem("inCheckoutPage");

    if (!document.location.href.includes("pinecove")) {
      const title = `${this.cmpAppSettings.getAppSetting("companyName")} - Customer Portal`;
      this.titleService.setTitle(title);
    }
    this.layoutService.isShadowAccount();
    if (!this.localStorageService.getItem("autoShowHelpBubble")) {
      setTimeout(() => {
        this.expandHelpText = true;
      }, 5000);
    }
  }

  /**
   * @method checkToken
   * @description checks for token if no token clears storage else checks access url is empty and assigns the url
   */
  private checkToken() {
    this.authService.getToken().subscribe((token) => {
      if (token) {
        const isStaff = localStorage.getItem("staffUserLoggedIn") === "true";
        if (isStaff) {
          this.layoutService.redirectToStaff();
        } else {
          this.layoutService.redirectToCamper();
        }
      }
    });
  }

  /**
   * @method loadPortalSettings
   * @description load portal settings for displaying company name
   */
  private loadPortalSettings() {
    this.layoutService.loadingData = true;
    this.layoutService.loadPortalSettings().then((settings) => {
      this.layoutService.loadingData = false;
      if (settings.buttonStyle === "pill") {
        let bodyElement: HTMLElement | null = null;
        bodyElement =
          this.elRef.nativeElement.closest("body") !== null
            ? this.elRef.nativeElement.closest("body")
            : this.elRef.nativeElement.ownerDocument.body;
        this.renderer.addClass(bodyElement, "button-style-pill");
      }
      this.logo = settings.logo;
      const backgroundImage = settings.background
        ? environment.s3AssetEnv + settings.background
        : `${environment.assetPrefix}/assets/images/default-background.jpg`;
      const style = this.renderer.createElement(`style`);
      const text = this.renderer.createText(
        `:root { --image-page-background: url('${backgroundImage}'); }`,
      );
      this.renderer.appendChild(style, text);
      this.renderer.setProperty(style, "type", "text/css");
      this.renderer.appendChild(
        this.elRef.nativeElement.ownerDocument.head,
        style,
      );

      // set image used in special-layout-1 container for mobile design
      const mobileStyle = this.renderer.createElement(`style`);
      const mobileText = this.renderer.createText(
        `:root { --image-sl1-top-background: url('${backgroundImage}'); }`,
      );
      this.renderer.appendChild(mobileStyle, mobileText);
      this.renderer.setProperty(mobileStyle, "type", "text/css");
      this.renderer.appendChild(
        this.elRef.nativeElement.ownerDocument.head,
        mobileStyle,
      );
      if (settings.background && settings.backgroundImageColorize === "") {
        // background-not-colorized
        this.renderer.addClass(
          this.elRef.nativeElement.querySelectorAll(".page-wrap")[0],
          "background-not-colorized",
        );
      }
    });
  }

  /**
   * @method configureSearchEngineIndexing
   * @description configure search engine indexing based on the SEO settings
   */
  private async configureSearchEngineIndexing() {
    const seoSettings = await this.layoutService.loadSEOSettings();
    const content = seoSettings.enabled ? "noindex, nofollow" : "index, follow";
    const existingTag = this.metaService.getTag('name="robots"');
    if (existingTag) {
      this.metaService.updateTag({ name: "robots", content });
    } else {
      this.metaService.addTag({ name: "robots", content });
    }
  }

  /**
   * @method setCanonicalURL
   * @description set canonical url for custom domain
   */
  private async setCanonicalURL() {
    const url = await this.layoutService.checkCustomDomain();
    if (url.isUsingCustomDomain) {
      const link: HTMLLinkElement = this.renderer.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", document.location.href);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  /**
   * @method logOut
   * @description Clear the localStorage on logging out the user
   * @returns void
   */
  public async logOut() {
    this.layoutService.logOutStatus = true;
    this.layoutService.accessUrl = "/";
    if (this.layoutService.userDetail) {
      this.rememberCamper["email"] = this.layoutService.userDetail.email;
      this.rememberCamper["firstName"] =
        this.layoutService.userDetail.firstName;
    }
    this.authService.logOut();
    this.layoutService.loggedIn = false;
    this.localStorageService.setItem(
      "remember_consumer",
      JSON.stringify(this.rememberCamper),
    );
    const tenantInfo = await this.cmpEnv.getTenantInfo();
    window.location.href = tenantInfo.camperPortalUrl.startsWith("http")
      ? tenantInfo.camperPortalUrl
      : `https://${tenantInfo.camperPortalUrl}`;
  }

  /**
   * @method setLoaderText
   * @description set loading text in account register loader
   * @returns array of Observables
   */
  private setLoaderText() {
    const loadingSequenceText: Array<string> = [
      `We're excited you're here…`,
      `Preparing your profile…`,
      `Almost there…`,
      `Configuring your settings…`,
    ];
    this.currentLoadingText$ = interval(3000).pipe(
      take(loadingSequenceText.length),
      map((index: number) => loadingSequenceText[index]),
      startWith(`We're creating your account…`),
    );
  }
}
